import React, { useState, useEffect, useRef } from "react";
import { Link } from "gatsby";
import styled from "styled-components";
import { motion, useScroll } from "framer-motion";

const Hamburger = styled.button`
  width: 20px;
  height: 20px;
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;

  span {
    display: block;
    position: absolute;
    width: 100%;
    height: 1px;
    background: currentColor;
    transition: all 0.3s ease;
    transform-origin: center;

    &:nth-child(1) {
      top: 4px;
      transform: ${(props) =>
        props.isOpen ? "translateY(6px) rotate(45deg)" : "none"};
    }

    &:nth-child(2) {
      top: 9px;
      opacity: ${(props) => (props.isOpen ? "0" : "1")};
    }

    &:nth-child(3) {
      top: 14px;
      transform: ${(props) =>
        props.isOpen ? "translateY(-4px) rotate(-45deg)" : "none"};
    }
  }
`;

const Navigation = styled.nav`
  padding: 10px 20px;

  & .top-level {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    & button,
    & a {
      display: block;
      margin: 0;

      appearance: none;
      overflow: visible;

      line-height: 40px;
    }
  }

  & ol {
    width: 100%;

    & li {
      &::after {
        content: ", ";
        padding: 0 10px 0 0;
      }

      &:last-of-type {
        &::after {
          content: "";
        }
      }

      & a {
        &.current {
          font-style: italic;
        }
      }
    }
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const LogoContainer = styled.div`
  display: flex;
  position: relative;
`;

const PHRASE = "Painters Painting Paintings.";
const P_INDICES = [0, 9, 18];

// Debounce utility
function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export const MobileMenu = ({ links, location, currentExhibition }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const letters = PHRASE.split("");
  const [scroll, setScroll] = useState(0);
  const letterRefs = useRef([]);
  const pppRefs = useRef([]);
  const [startPPositions, setStartPPositions] = useState([0, 0, 0]);
  const [finalPPositions, setFinalPPositions] = useState([0, 0, 0]);
  const [isMeasured, setIsMeasured] = useState(false);
  const logoContainerRef = useRef();
  const hiddenContainerRef = useRef();

  // Function to measure P positions (does not set isMeasured - that's controlled separately)
  const measurePPositions = React.useCallback((setReady = false) => {
    if (!logoContainerRef.current) return;

    const containerRect = logoContainerRef.current.getBoundingClientRect();
    if (containerRect.width === 0) return; // Don't measure if not yet visible

    const startPs = P_INDICES.map((idx) => {
      const ref = letterRefs.current[idx];
      return ref ? ref.getBoundingClientRect().left - containerRect.left : 0;
    });
    setStartPPositions(startPs);

    const finalPs = [0, 1, 2].map((idx) => {
      const ref = pppRefs.current[idx];
      return ref ? ref.getBoundingClientRect().left - containerRect.left : 0;
    });
    setFinalPPositions(finalPs);

    // Only mark as measured when explicitly told (after fonts load and layout settled)
    if (setReady) {
      setIsMeasured(true);
    }
  }, []);

  // Debounced version for resize/scroll (only updates positions, doesn't change isMeasured)
  const debouncedMeasure = React.useRef(
    debounce(() => measurePPositions(false), 100)
  ).current;

  useEffect(() => {
    // Wait for refs to be attached and layout to settle
    const measureWithDelay = (setReady = false) => {
      if (
        logoContainerRef.current &&
        letterRefs.current[0] &&
        pppRefs.current[0]
      ) {
        // Multiple RAFs to ensure browser has painted and settled layout
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => measurePPositions(setReady));
          });
        });
      }
    };

    // Initial measure after mount
    measureWithDelay(false);

    // Only set isMeasured=true after fonts are loaded to ensure accurate positioning
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        // Small extra delay after fonts ready to ensure reflow is complete
        setTimeout(() => measureWithDelay(true), 150);
      });
    } else {
      setTimeout(() => measureWithDelay(true), 300);
    }

    // Use ResizeObserver for more reliable measurement updates
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && logoContainerRef.current) {
      resizeObserver = new ResizeObserver(() => debouncedMeasure());
      resizeObserver.observe(logoContainerRef.current);
    }

    window.addEventListener("resize", debouncedMeasure);
    window.addEventListener("scroll", debouncedMeasure, true);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", debouncedMeasure);
      window.removeEventListener("scroll", debouncedMeasure, true);
    };
  }, [measurePPositions, debouncedMeasure]);

  useEffect(() => {
    const unsubscribe = scrollY.onChange((latest) => {
      setScroll(latest);
    });
    return () => unsubscribe();
  }, [scrollY]);

  function getLogoOpacity() {
    if (scroll > 195) return 0;
    if (scroll > 100 && scroll <= 195) {
      return 1 - (scroll - 100) / 95;
    }
    return 1;
  }

  function getRedPLeft(i) {
    if (scroll <= 200) {
      return startPPositions[i];
    }
    if (scroll > 200 && scroll <= 300) {
      const progress = (scroll - 200) / 100;
      const start = startPPositions[i];
      const end = finalPPositions[i];
      return start + (end - start) * progress;
    }
    return finalPPositions[i];
  }

  return (
    <Navigation
      className={`display-flex space-between menu-${
        isMenuOpen ? `open` : `closed`
      }`}
    >
      <div className="top-level">
        {/* Hidden PPP for measurement */}
        <div
          style={{
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            top: 0,
          }}
        >
          <div ref={hiddenContainerRef} style={{ position: "relative" }}>
            {[..."PPP"].map((letter, i) => (
              <span key={i} ref={(el) => (pppRefs.current[i] = el)}>
                {letter}
              </span>
            ))}
          </div>
        </div>
        {/* Full logo for measurement and display */}
        <StyledLink to="/">
          <LogoContainer
            ref={logoContainerRef}
            style={{ position: "relative" }}
          >
            <motion.div
              style={{
                display: "flex",
                alignItems: "center",
                opacity: getLogoOpacity(),
              }}
            >
              {letters.map((letter, i) => {
                const isPLetter = P_INDICES.includes(i);
                return (
                  <motion.span
                    key={`logo-letter-${i}`}
                    ref={(el) => (letterRefs.current[i] = el)}
                    style={{
                      opacity: 1,
                      fontWeight: "normal",
                      marginRight: letter === " " ? 0 : 0,
                      minWidth: letter === " " ? "0.5em" : undefined,
                      // Hide inline P's once absolute P's are rendered to prevent doubling
                      visibility:
                        isPLetter && isMeasured ? "hidden" : "visible",
                    }}
                  >
                    {letter === " " ? "\u00A0" : letter}
                  </motion.span>
                );
              })}
            </motion.div>
            {/* Absolute red P's at animated positions */}
            {isMeasured &&
              startPPositions.map((_, i) => (
                <span
                  key={`abs-p-${i}`}
                  style={{
                    position: "absolute",
                    left: getRedPLeft(i),
                    top: 0,
                    pointerEvents: "none",
                    zIndex: 10,
                    fontSize: "inherit",
                    transition: "left 0.3s cubic-bezier(0.4,0,0.2,1)",
                  }}
                >
                  P
                </span>
              ))}
          </LogoContainer>
        </StyledLink>
        <Hamburger
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          isOpen={isMenuOpen}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          <span />
          <span />
          <span />
        </Hamburger>
      </div>

      {isMenuOpen && (
        <ol className="display-flex">
          {currentExhibition !== null && (
            <li className="exhibition">
              <Link to={currentExhibition} activeClassName="current">
                Exhibition
              </Link>
            </li>
          )}

          {links}
        </ol>
      )}
    </Navigation>
  );
};
